import {useState, useCallback, useRef} from 'react';

// 定义获取数据函数的参数类型
interface FetchDataParams {
  page: number;
  pageSize: number;
}

// 定义 hook 的参数类型
interface UseInfiniteListParams<T> {
  fetchData: (params: FetchDataParams) => Promise<T[]>;
  pageSize?: number;
  initialData?: T[];
}

// 定义 hook 的返回类型
interface UseInfiniteListReturn<T> {
  items: T[];
  hasMore: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
}

const useInfiniteList = <T = any>({
  fetchData,
  pageSize = 20,
  initialData = [],
}: UseInfiniteListParams<T>): UseInfiniteListReturn<T> => {
  const [items, setItems] = useState<T[]>(initialData);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageRef = useRef(1);

  // 加载更多
  const loadMore = useCallback(async () => {
    if (isLoading || isRefreshing) return;

    setIsLoading(true);
    try {
      const newData = await fetchData({
        page: pageRef.current,
        pageSize,
      });

      if (newData.length < pageSize) {
        setHasMore(false);
      }

      setItems(prev => [...prev, ...newData]);
      pageRef.current += 1;
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, pageSize, isLoading, isRefreshing]);

  // 下拉刷新
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    pageRef.current = 1;

    try {
      const newData = await fetchData({
        page: 1,
        pageSize,
      });

      setItems(newData);
      setHasMore(newData.length >= pageSize);
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchData, pageSize]);

  // 重置数据
  const reset = useCallback(() => {
    setItems([]);
    pageRef.current = 1;
    setHasMore(true);
    setIsLoading(false);
    setIsRefreshing(false);
  }, []);

  return {
    items,
    hasMore,
    isLoading,
    isRefreshing,
    loadMore,
    refresh,
    reset,
    setItems, // 提供直接设置数据的能力
  };
};

export default useInfiniteList;
